/**
 * 利用ガイドの共通レイアウト。
 * 左サイドバー（ネストした目次）+ 右本文の 2 カラム。
 *
 * - 公開ページ用 (PublicHeader + PublicFooter)
 * - スマホでは目次が折りたたまれる
 */
import type { ReactNode } from "react";
import { useState } from "react";
import { PublicHeader, PublicFooter } from "../LandingPage.js";
import { TOC_SECTIONS, type GuideSection } from "./GuideToc.js";

const PRIMARY = "#534AB7";
const SIDEBAR_WIDTH = 260;

interface Props {
  currentSlug: string | null;
  title: string;
  updatedAt?: string;
  children: ReactNode;
}

export function GuideLayout({ currentSlug, title, updatedAt, children }: Props) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div style={{
      fontFamily: "system-ui, -apple-system, sans-serif",
      color: "#1a1a1a",
      background: "white",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    }}>
      <PublicHeader minimal />

      {/* モバイル用：目次トグル（@media で表示制御するため display は CSS 側で管理） */}
      <div className="guide-mobile-toggle" style={{ padding: "12px 20px", borderBottom: "1px solid #e8e7e3", background: "#faf9f7" }}>
        <button
          onClick={() => setMobileOpen((v) => !v)}
          style={{
            background: "white",
            border: "1px solid #d0cfc9",
            borderRadius: 6,
            padding: "6px 12px",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          {mobileOpen ? "目次を閉じる" : "目次を開く"}
        </button>
      </div>

      <div style={{ display: "flex", maxWidth: 1280, margin: "0 auto", padding: "0 0 60px", alignItems: "flex-start", flex: 1, width: "100%" }}>
        {/* サイドバー（スクロール追従） */}
        <aside
          className={mobileOpen ? "guide-sidebar-open" : "guide-sidebar"}
          style={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            borderRight: "1px solid #e8e7e3",
            padding: "30px 24px",
            position: "sticky",
            top: 0,
            maxHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <Sidebar currentSlug={currentSlug} onNavigate={() => setMobileOpen(false)} />
        </aside>

        {/* 本文 */}
        <main className="guide-main" style={{ flex: 1, padding: "40px 48px", minWidth: 0 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 12, lineHeight: 1.4 }}>{title}</h1>
          {updatedAt && (
            <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 28 }}>更新日: {updatedAt}</div>
          )}
          <div style={{ fontSize: 15, lineHeight: 1.9, color: "#2a2a28" }}>{children}</div>
        </main>
      </div>

      <PublicFooter />

      {/* レイアウト調整 */}
      <style>{`
        /* 箇条書きのマーカー（•）が見出しより左に飛び出さないように、
           ul/ol の左パディングを抑える */
        .guide-main ul,
        .guide-main ol {
          padding-left: 1.4em;
          margin-top: 8px;
          margin-bottom: 8px;
        }
        .guide-main ul ul,
        .guide-main ul ol,
        .guide-main ol ul,
        .guide-main ol ol {
          padding-left: 1.4em;
          margin-top: 4px;
          margin-bottom: 4px;
        }
        .guide-main li {
          margin: 4px 0;
        }

        /* モバイル: 880px 以下のみトグル/サイドバー非表示制御 */
        .guide-mobile-toggle { display: none; }
        @media (max-width: 880px) {
          .guide-mobile-toggle { display: block; }
          .guide-sidebar { display: none; }
          .guide-sidebar-open {
            display: block !important;
            width: 100% !important;
            border-right: none !important;
            border-bottom: 1px solid #e8e7e3;
            position: static !important;
            max-height: none !important;
          }
        }
      `}</style>
    </div>
  );
}

function Sidebar({ currentSlug, onNavigate }: { currentSlug: string | null; onNavigate: () => void }) {
  return (
    <nav>
      <a
        href="/guide"
        onClick={onNavigate}
        style={{
          display: "block",
          fontSize: 16,
          fontWeight: 700,
          color: PRIMARY,
          textDecoration: "none",
          marginBottom: 18,
        }}
      >
        利用ガイド
      </a>
      {TOC_SECTIONS.map((section) => (
        <SectionBlock
          key={section.id}
          section={section}
          currentSlug={currentSlug}
          onNavigate={onNavigate}
        />
      ))}
    </nav>
  );
}

function SectionBlock({ section, currentSlug, onNavigate }: { section: GuideSection; currentSlug: string | null; onNavigate: () => void }) {
  const containsCurrent = section.articles.some((a) => a.slug === currentSlug);
  const [open, setOpen] = useState(containsCurrent || section.id === "start");

  return (
    <div style={{ marginBottom: 8 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          textAlign: "left",
          background: "none",
          border: "none",
          padding: "8px 4px",
          fontSize: 14,
          fontWeight: 600,
          color: "#2a2a28",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{section.title}</span>
        <span style={{ fontSize: 10, color: "#999", transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
      </button>
      {open && (
        <ul style={{ listStyle: "none", padding: "4px 0 8px 12px", margin: 0 }}>
          {section.articles.map((a) => {
            const isActive = a.slug === currentSlug;
            return (
              <li key={a.slug}>
                <a
                  href={`/guide/${a.slug}`}
                  onClick={onNavigate}
                  style={{
                    display: "block",
                    padding: "6px 10px",
                    fontSize: 13,
                    color: isActive ? PRIMARY : "#4a4a48",
                    background: isActive ? "#F1EEFC" : "transparent",
                    borderLeft: isActive ? `3px solid ${PRIMARY}` : "3px solid transparent",
                    fontWeight: isActive ? 600 : 400,
                    textDecoration: "none",
                    borderRadius: "0 6px 6px 0",
                  }}
                >
                  {a.title}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
